<template>
  <div>
    <div v-if="assets.length > 0" class="list-actions">
      <div class="select">
        <ts-link @click="handleSelectAllClick">Select all</ts-link>
        <ts-link @click="handleUnselectAllClick">Unselect all</ts-link>
      </div>
      <div class="actions" v-show="hasSelectedAssets">
        <ts-button size="small" @click="handleDeleteAssetsClick(selectedAssets)"> Delete </ts-button>
        <ts-button :pending="!canDownloadAssets" size="small" @click="handleDownloadAssetsClick(selectedAssets)">
          Download
        </ts-button>
      </div>
    </div>
    <div ref="droptarget" class="droptarget" @dragover="isDragging = true" @dragleave="isDragging = false">
      <div class="list">
        <div v-for="asset in assets" :key="asset.id" class="row">
          <ts-checkbox
            class="checkbox"
            :value="isAssetSelected(asset)"
            @input="handleAssetSelectedChange(asset, $event)"
          ></ts-checkbox>
          <div class="file-info">
            <p class="file-name">
              {{ asset.name }}
            </p>
            <div v-if="isAssetUploading(asset)">
              <ts-progress :percentage="asset.resumableFile.tsUploadProgress * 100"></ts-progress>
            </div>
            <p v-else class="file-creation-info">
              Created on {{ formatDate(asset.createdAt) }} by {{ asset.createdBy.name }}
            </p>
          </div>
          <div class="actions">
            <div v-if="!isAssetUploading(asset)" class="actions">
              <ts-button
                v-if="asset.type === 'uploaded-file'"
                size="small"
                circle
                :pending="isPending(asset.status)"
                @click="handleDownloadAssetClick(asset)"
              >
                <ts-icon
                  icon="save"
                  size="x-small"
                  :pending="isPending(asset.status)"
                  :title="isPending(asset.status) ? 'Pending' : 'Download'"
                  color="#6d7587"
                />
              </ts-button>
              <FilePreview
                v-if="asset.type === 'uploaded-file' && isFilePreviewable(asset.file)"
                :file="asset.file"
                :show-edit="false"
                @delete="handleDeleteAssetsClick([asset])"
              />
              <ts-button v-if="asset.type === 'google-drive-link'" size="small" circle @click="handleLinkCopy(asset)">
                <ts-icon icon="link" size="x-small" color="#6d7587" title="Copy link" />
              </ts-button>
              <EditAsset :value="asset" @input="handleAssetEdit" />
            </div>
            <ts-button size="small" circle @click="handleDeleteAssetsClick([asset])">
              <ts-icon icon="trash" size="x-small" color="#6d7587" title="Delete" />
            </ts-button>
          </div>
        </div>
      </div>
      <div v-if="isProjectManager || isCustomer" ref="droptargetPlaceholder" class="placeholder">
        <DropTarget :label="label" :active="isDragging" :show-dropin="assets.length === 0" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.list-actions {
  margin-bottom: 20px;
  display: flex;

  .select {
    flex-grow: 1;

    > * {
      margin-right: 23px;
    }
  }

  .actions {
    > * {
      margin-left: 8px;
    }
  }
}

.droptarget {
  .placeholder {
    display: flex;
    justify-content: center;
    margin: 50px;
  }
}

.row {
  background: #ebecf0;
  background-image: linear-gradient(-42deg, rgba(0, 0, 0, 0.4) 0%, rgba(255, 255, 255, 0.4) 100%);
  background-blend-mode: soft-light, normal;
  background-clip: padding-box;
  border: 2px solid rgba(255, 255, 255, 0.16);
  box-shadow: -2px -2px 6px 0 rgba(250, 251, 255, 0.48), 2px 2px 6px 0 rgba(166, 171, 189, 0.24);
  border-radius: 16px;
  margin-bottom: 10px;

  display: flex;
  align-items: center;
  padding: 22px 18px;

  .checkbox {
    margin-right: 13px;
  }

  .file-info {
    flex-grow: 1;
  }

  .file-name {
    font-family: Roboto;
    font-size: 16px;
    color: #6d7587;
    overflow-wrap: break-word;
  }

  .file-creation-info {
    font-family: Roboto;
    font-size: 10px;
    line-height: 16px;
    color: #acbacc;
  }

  .actions {
    display: flex;
    align-items: center;

    > * {
      margin-left: 10px;
    }
  }
}
</style>

<script>
import EditAsset from './EditAsset.vue';
import DropTarget from '@/components/DropTarget.vue';
import FilePreview from '@/components/FilePreview.vue';

import projectViewHelpers from '@/mixins/projectViewHelpers';
import uploader from '@/mixins/uploader';

export default {
  mixins: [projectViewHelpers, uploader],
  components: {
    EditAsset,
    DropTarget,
    FilePreview
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    assets: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedAssets: [],
      isDragging: false
    };
  },
  watch: {
    assets(value) {
      this.selectedAssets = [];
    }
  },
  computed: {
    hasSelectedAssets() {
      return this.selectedAssets.length > 0;
    },
    canDownloadAssets() {
      return (
        this.selectedAssets.length > 0 &&
        this.selectedAssets.every(asset => {
          return asset.file?.status === 'processed';
        })
      );
    }
  },
  methods: {
    isAssetSelected(asset) {
      return !!this.selectedAssets.find(a => a.id === asset.id);
    },
    isAssetUploading(asset) {
      return asset.resumableFile && !asset.resumableFile.tsIsComplete;
    },
    handleSelectAllClick() {
      this.selectedAssets = this.assets;
    },
    handleUnselectAllClick() {
      this.selectedAssets = [];
    },
    handleAssetSelectedChange(asset, value) {
      if (value) {
        this.selectedAssets = [...this.selectedAssets, asset];
      } else {
        this.selectedAssets = this.selectedAssets.filter(a => a.id !== asset.id);
      }
    },
    handleDownloadAssetClick(asset) {
      if (this.isPending(asset.status)) {
        window.EventBus.$emit('download.pending');
        return;
      }
      window.open(asset.file.url, '_blank');
    },
    handleAssetEdit(asset) {
      this.$emit('asset-edit', asset);
    },
    handleDeleteAssetsClick(assets) {
      assets
        .filter(asset => !!asset.resumableFile)
        .forEach(asset => {
          this.resumable.removeFile(asset.resumableFile);
          this.$emit('asset-delete', asset);
        });
      assets
        .filter(asset => !asset.resumableFile)
        .forEach(asset => {
          this.$emit('asset-delete', asset);
        });
    },
    async handleLinkCopy(asset) {
      try {
        await this.$copyText(asset.url);
        this.showInfo('Link copied to clipboard');
      } catch (err) {
        console.error('Error when copying step link', err);
        this.showError('Error when copying step link');
      }
    },
    handleDownloadAssetsClick(assets) {
      if (!this.canDownloadAssets) {
        window.EventBus.$emit('download.pending');
        return;
      }
      const uploadedAssets = assets.filter(asset => !asset.resumableFile);
      if (uploadedAssets.length > 1) {
        this.showDownloadProcessing();
        this.$emit('assets-download', uploadedAssets);
        return;
      }

      uploadedAssets.length === 1 && this.handleDownloadAssetClick(uploadedAssets[0]);
    }
  },
  mounted() {
    if (this.isProjectManager || this.isCustomer) {
      this.resumable.assignBrowse(this.$refs.droptargetPlaceholder);
      this.resumable.assignDrop(this.$refs.droptarget);
    }
    this.resumable.on('fileAdded', file => {
      this.$emit('file-added', file);
      this.resumable.upload();
    });
    this.resumable.on('fileSuccess', file => {
      this.$emit('file-success', file);
    });
    this.resumable.on('fileError', (file, message) => {
      this.$emit('file-error', file, message);
    });
  }
};
</script>
